export const useProductPage = async () => {
  const nuxtApp = useNuxtApp()
  const {
    breadcrumbs,
    asyncCategory,
    items: products,
    page,
    perPage,
    total,
  } = await useListApi<Product>({
    categoryApiPath: '/product-categories',
    itemPath: '/products?populate=productCategory&orderBy=position&sortBy=asc',
    categoryField: 'productCategoryId',
    componentName: 'Product',
    hasCategory: true,
    defaultPerPage: 24,
  })

  const { getPath, slugPageInfo, domain } = useNavigationMenu()
  const { isMainLanguage } = useLanguage()
  const currentCategory = computed(() => asyncCategory.value?.category)

  useCustomSeo({
    title: () => currentCategory.value?.metaTitleFront || slugPageInfo.value?.metaTitleFront,
    description: () =>
      currentCategory.value?.metaDescriptionFront ||
      slugPageInfo.value?.metaDescriptionFront ||
      slugPageInfo.value?.description,
    image: () => currentCategory.value?.ogImage?.large || slugPageInfo.value?.ogImage?.large,
    noindex: () => !isMainLanguage.value && products.value.length === 0,
  })

  const categoryEventName = 'Product / Service'
  const eventName = 'click_product_category'

  const { analyticEvent, fbPixelEvent } = useAnalytic()
  const eventProduct = (product: string) => {
    analyticEvent({
      ga4ActionName: 'click_product',
      ga4Label: product,
    })
    fbPixelEvent({
      fbCategory: 'trackCustom',
      fbEvent: categoryEventName,
      fbValue: { label: product },
    })
  }

  const { currentLocaleCode } = useLanguage()
  const currencyStore = useCurrencyStore()
  const currencyName = computed(() => currencyStore.currentName)
  nuxtApp.runWithContext(() => useSchemaOrg([
    defineWebPage({
      '@type': 'CollectionPage',
      name: currentCategory.value?.metaTitleFront || slugPageInfo.value?.metaTitleFront,
      '@id': `${domain.value}${getPath({ componentName: 'Product' })}#webpage`,
      url: `${domain.value}${getPath({ componentName: 'Product' })}`,
      inLanguage: currentLocaleCode.value,
      isPartOf: { '@id': `${domain.value}/#website` },
      publisher: { '@id': `${domain.value}/#organization` },
      mainEntity: products.value.map(product => ({
        '@type': 'Product',
        name: product.title,
        url: `${domain.value}${getPath({ componentName: 'Product', slug: product.slug })}`,
        brand: { '@id': `${domain.value}/#organization` },
        image: product.imageCover?.large || product.imageCover?.medium || product.imageCover?.small || '',
        offers: product.priceOfDiscounted && product.priceOfDiscounted < product.price
          ? {
              '@type': 'AggregateOffer',
              lowPrice: product.priceOfDiscounted || 0,
              highPrice: product.price || 0,
              priceCurrency: currencyName.value,
              availability: 'https://schema.org/InStock',
              url: `${domain.value}${getPath({ componentName: 'Product', slug: product.slug })}`,
            }
          : {
              '@type': 'Offer',
              price: product.price || 0,
              priceCurrency: currencyName.value,
              availability: 'https://schema.org/InStock',
              url: `${domain.value}${getPath({ componentName: 'Product', slug: product.slug })}`,
            },
      })),
    }),
    defineItemList({
      '@id': `${domain.value}/product#category-list`,
      itemListElement: asyncCategory.value?.categoryButtonItems?.map((cat, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: cat.title,
        url: `${domain.value}${cat.path}`,
      })),
    }),
  ]))

  return {
    breadcrumbs,
    asyncCategory,
    products,
    page,
    perPage,
    total,
    getPath,
    categoryEventName,
    eventName,
    eventProduct,
  }
}
